import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { guardarDestino } from '@/lib/destino';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import {
  estadoDoBloco,
  textoLojaEstado,
  usePerfilEvento,
  type ChaveBloco,
  type PerfilEvento,
} from '@/lib/perfilEvento';
import type { FormaParticipar } from '@/pages/assistente-evento/types';
import { carregarEdicao, type DadosEdicao } from './carregar';
import CartaoBloco from './components/CartaoBloco';
import PainelEditor from './components/PainelEditor';
import MaisRecursos from './components/MaisRecursos';
import OQueEditor from './components/OQueEditor';
import QuandoOndeEditor from './components/QuandoOndeEditor';
import ParticipacoesEditor from './components/ParticipacoesEditor';
import DinheiroEditor from './components/DinheiroEditor';
import FichaEditor from './components/FichaEditor';
import EncontrosEditor from './components/EncontrosEditor';
import DivulgacaoEditor from './components/DivulgacaoEditor';

type Estado = 'carregando' | 'negado' | 'nao-encontrado' | 'erro' | 'ok';

interface CartaoDef {
  bloco: ChaveBloco;
  icone: string;
  titulo: string;
  resumo?: string;
}

// Os cartões, na ordem, só os blocos que o perfil manda mostrar.
function cartoesDoPerfil(perfil: PerfilEvento): CartaoDef[] {
  const r = perfil.rotulos;
  const defs: CartaoDef[] = [
    { bloco: 'o_que', icone: 'ri-article-line', titulo: 'O que é' },
    { bloco: 'quando_onde', icone: 'ri-calendar-event-line', titulo: 'Quando e onde' },
    {
      bloco: 'participacoes',
      icone: 'ri-ticket-2-line',
      titulo: r.participacoes,
      resumo: `${perfil.situacao.participacoes ?? 0} cadastrada(s)`,
    },
    { bloco: 'dinheiro', icone: 'ri-money-dollar-circle-line', titulo: 'O dinheiro' },
    { bloco: 'ficha', icone: 'ri-file-list-3-line', titulo: 'A ficha de inscrição' },
    { bloco: 'encontros', icone: 'ri-calendar-check-line', titulo: 'Encontros e datas' },
    { bloco: 'equipes', icone: 'ri-team-line', titulo: 'Equipes de serviço' },
    {
      bloco: 'loja',
      icone: 'ri-store-2-line',
      titulo: `Fichas e ${r.pontos.toLowerCase()}`,
      resumo: textoLojaEstado(perfil.situacao.loja_estado),
    },
    { bloco: 'rifa', icone: 'ri-gift-2-line', titulo: 'Ação entre amigos' },
    { bloco: 'divulgacao', icone: 'ri-megaphone-line', titulo: 'Divulgação' },
  ];
  return defs.filter((d) => perfil.blocos[d.bloco]);
}

export default function EditarEventoPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, pronto } = useAuth();
  const eventoIdNum = Number(id);
  const eventoIdValido = Number.isFinite(eventoIdNum) && eventoIdNum > 0;

  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState<string | null>(null);
  const [dados, setDados] = useState<DadosEdicao | null>(null);
  const [blocoAberto, setBlocoAberto] = useState<ChaveBloco | null>(null);

  const { perfil, carregando: carregandoPerfil, erro: erroPerfil, recarregar, definirRecurso } =
    usePerfilEvento(eventoIdValido ? eventoIdNum : null);

  const carregar = useCallback(async () => {
    if (!pronto) return;
    if (!user) {
      const destino = `/evento/${id}/editar`;
      guardarDestino(destino);
      navigate(`/entrar?voltar=${encodeURIComponent(destino)}`, { replace: true });
      return;
    }
    setEstado('carregando');
    setErro(null);
    try {
      if (!eventoIdValido) {
        setEstado('nao-encontrado');
        return;
      }

      const { data: ev, error: errEv } = await supabase
        .from('eventos')
        .select('id, dono_id')
        .eq('id', eventoIdNum)
        .maybeSingle();
      if (errEv) throw errEv;
      if (!ev) {
        setEstado('nao-encontrado');
        return;
      }

      let permitido = (ev as { dono_id: string }).dono_id === user.id;
      if (!permitido) {
        const { data: eq } = await supabase
          .from('equipe_evento')
          .select('id')
          .eq('evento_id', eventoIdNum)
          .eq('usuario_id', user.id)
          .eq('status', 'ativo')
          .maybeSingle();
        permitido = !!eq;
      }
      if (!permitido) {
        setEstado('negado');
        return;
      }

      const d = await carregarEdicao(eventoIdNum, user.id);
      setDados(d);
      setEstado('ok');
    } catch (e) {
      setErro(mensagemAmigavel(e));
      setEstado('erro');
    }
  }, [id, navigate, eventoIdNum, eventoIdValido, pronto, user]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  function aoSalvar() {
    recarregar();
  }

  function atualizarParticipacoes(novas: FormaParticipar[]) {
    setDados((prev) => (prev ? { ...prev, participacoes: novas } : prev));
  }

  function abrirBloco(bloco: ChaveBloco) {
    if (bloco === 'loja') {
      navigate(`/gerenciar/${eventoIdNum}/loja`);
      return;
    }
    if (bloco === 'equipes') {
      navigate(`/gerenciar/${eventoIdNum}/direcao?aba=equipes`);
      return;
    }
    if (bloco === 'rifa') {
      navigate('/minhas-rifas');
      return;
    }
    setBlocoAberto(bloco);
  }

  if (estado === 'carregando' || carregandoPerfil) {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[720px] px-4 py-8">
          <div className="h-6 w-56 animate-pulse rounded bg-secondary-100" />
          <div className="mt-6 space-y-3">
            <div className="h-16 w-full animate-pulse rounded-lg bg-secondary-100" />
            <div className="h-16 w-full animate-pulse rounded-lg bg-secondary-100" />
            <div className="h-16 w-full animate-pulse rounded-lg bg-secondary-100" />
          </div>
        </div>
      </div>
    );
  }

  if (estado === 'negado' || estado === 'nao-encontrado' || estado === 'erro') {
    const titulo =
      estado === 'negado'
        ? 'Sem acesso'
        : estado === 'nao-encontrado'
          ? 'Evento não encontrado'
          : 'Não foi possível carregar';
    const msg =
      estado === 'negado'
        ? 'Você não tem acesso a este evento.'
        : estado === 'nao-encontrado'
          ? 'Este evento não existe.'
          : erro;
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-lg border border-secondary-200 bg-white">
          <div className="h-[10px] w-full bg-primary-500" />
          <div className="p-8 text-center">
            <h1 className="text-xl font-normal text-foreground-950">{titulo}</h1>
            <p className="mt-2 text-sm text-secondary-500">{msg}</p>
            <button
              type="button"
              onClick={() => (estado === 'erro' ? carregar() : navigate('/meus-eventos'))}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              {estado === 'erro' ? 'Tentar de novo' : 'Voltar aos meus eventos'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!perfil || !dados) {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[720px] px-4 py-8">
          <p className="text-sm text-secondary-500">{erroPerfil || 'Carregando…'}</p>
        </div>
      </div>
    );
  }

  const ev = dados.evento;
  const nome = (ev.nome as string) || 'Evento';
  const cartoes = cartoesDoPerfil(perfil);
  const ehIngresso = perfil.chaves.vende_ingressos;

  return (
    <div className="min-h-screen bg-background-100">
      <header className="fixed inset-x-0 top-0 z-20 border-b border-secondary-200 bg-white">
        <div className="mx-auto w-full max-w-[720px] px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate(`/gerenciar/${eventoIdNum}`)}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm font-medium text-foreground-700 transition-colors hover:text-foreground-950"
            >
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-arrow-left-line" />
              </span>
              Painel
            </button>
          </div>
          <h1 className="mt-2 truncate text-lg font-medium text-foreground-950">{nome}</h1>
          <p className="text-xs text-secondary-500">Editar evento</p>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[720px] px-4 pb-16 pt-24">
        {erroPerfil && (
          <div className="mb-4 rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
            {erroPerfil}
          </div>
        )}

        <div className="space-y-3">
          {cartoes.map((c) => (
            <CartaoBloco
              key={c.bloco}
              icone={c.icone}
              titulo={c.titulo}
              resumo={c.resumo}
              estado={estadoDoBloco(perfil, c.bloco)}
              aoAbrir={() => abrirBloco(c.bloco)}
            />
          ))}
        </div>

        <div className="mt-6">
          <MaisRecursos perfil={perfil} definirRecurso={definirRecurso} />
        </div>
      </main>

      {blocoAberto === 'o_que' && (
        <PainelEditor titulo="O que é" aoFechar={() => setBlocoAberto(null)}>
          <OQueEditor
            eventoId={eventoIdNum}
            nomeInicial={(ev.nome as string) || ''}
            descricaoInicial={(ev.descricao as string) || ''}
            categoriaInicial={(ev.categoria as string) || ''}
            capaInicial={
              (ev.imagem_mobile_url as string) || (ev.imagem_desktop_url as string) || null
            }
            aoFechar={() => setBlocoAberto(null)}
            aoSalvo={aoSalvar}
          />
        </PainelEditor>
      )}

      {blocoAberto === 'quando_onde' && (
        <PainelEditor titulo="Quando e onde" aoFechar={() => setBlocoAberto(null)}>
          <QuandoOndeEditor
            eventoId={eventoIdNum}
            inicioEm={(ev.inicio_em as string) || null}
            fimEm={(ev.fim_em as string) || null}
            localNome={(ev.local_nome as string) || ''}
            endereco={(ev.endereco as string) || ''}
            cidade={(ev.cidade as string) || ''}
            uf={(ev.uf as string) || ''}
            cep={(ev.cep as string) || ''}
            aoFechar={() => setBlocoAberto(null)}
            aoSalvo={aoSalvar}
          />
        </PainelEditor>
      )}

      {blocoAberto === 'participacoes' && (
        <PainelEditor
          titulo={perfil.rotulos.participacoes}
          aoFechar={() => setBlocoAberto(null)}
        >
          <ParticipacoesEditor
            eventoId={eventoIdNum}
            participacoes={dados.participacoes}
            slug={(ev.slug as string) || ''}
            ehIngresso={ehIngresso}
            aoFechar={() => setBlocoAberto(null)}
            aoSalvo={aoSalvar}
          />
        </PainelEditor>
      )}

      {blocoAberto === 'dinheiro' && (
        <PainelEditor titulo="O dinheiro" aoFechar={() => setBlocoAberto(null)}>
          <DinheiroEditor
            eventoId={eventoIdNum}
            taxas={dados.taxas}
            mostrarParcial={perfil.blocos.pagamento_parcial}
            aoSalvo={aoSalvar}
          />
        </PainelEditor>
      )}

      {blocoAberto === 'ficha' && (
        <PainelEditor titulo="A ficha de inscrição" aoFechar={() => setBlocoAberto(null)}>
          <FichaEditor
            participacoes={dados.participacoes}
            onChange={atualizarParticipacoes}
            aoFechar={() => setBlocoAberto(null)}
            aoSalvo={aoSalvar}
          />
        </PainelEditor>
      )}

      {blocoAberto === 'encontros' && (
        <PainelEditor titulo="Encontros e datas" aoFechar={() => setBlocoAberto(null)}>
          <EncontrosEditor
            eventoId={eventoIdNum}
            cidade={(ev.cidade as string) || ''}
            mostrarPresencas={perfil.blocos.presencas}
            aoFechar={() => setBlocoAberto(null)}
            aoSalvo={aoSalvar}
          />
        </PainelEditor>
      )}

      {blocoAberto === 'divulgacao' && (
        <PainelEditor titulo="Divulgação" aoFechar={() => setBlocoAberto(null)}>
          <DivulgacaoEditor
            eventoId={eventoIdNum}
            slug={(ev.slug as string) || ''}
            nome={nome}
            whatsappInicial={(ev.whatsapp_vendas as string) || ''}
            comoParticiparInicial={(ev.como_participar as string) || ''}
            sugerirTextoInicial={(ev.sugerir_texto as string) || ''}
            aoFechar={() => setBlocoAberto(null)}
            aoSalvo={aoSalvar}
          />
        </PainelEditor>
      )}
    </div>
  );
}